import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { isEmpty } from '@semios/app-platform-common'
import { translate } from 'i18n/i18n'
import type { Moment } from 'moment-timezone'
import { useCallback, useEffect, useState } from 'react'
import { apiFetch } from 'utils/apiFetch'
import { showNotification } from 'utils/showNotification'
import type { Contact, Group } from '../../../ContactsAndGroups/ContactsAndGroupsContext'
import { useGroupsAndContacts } from '../../../ContactsAndGroups/ContactsAndGroupsContext'
import type { AlertTypeId, AlertWithPossibleId, WeatherForecastAlertOnly } from '../../_types'
import { AlertSection } from '../../components/AlertSection'
import { AlertsSettings } from '../../settings/alertSettings'
import type { convertAPIAlertToInitivalValues } from '../_utils/convertAPIAlertToInitivalValues'
import { getAlertVariablesForWeatherForecastQuery } from '../_utils/getAlertVariablesForWeatherForecastQuery'
import { Contacts } from './Contacts/Contacts'
import { WeatherForecastCoverage } from './Coverage/WeatherForecastCoverage/WeatherForecastCoverage'
import { Name } from './Name/Name'
import { Scheduling } from './Scheduling/Scheduling'
import { WeatherForecastThreshold } from './Thresholds/WeatherForecastThreshold/WeatherForecastThreshold'

export type WeatherForecastAlertFormValues = Extract<
  ReturnType<typeof convertAPIAlertToInitivalValues>,
  { alertTypeId: AlertTypeId }
>

const isRuleValid = (
  rule: Partial<WeatherForecastAlertOnly['rules']>,
): rule is WeatherForecastAlertOnly['rules'] => {
  // check if all the keys are there
  if ('windGreaterThanOrEqualToUserUnits' in rule || 'hasRain' in rule) {
    return true
  }

  if (
    ('temperatureGreaterThanOrEqualToUserUnits' in rule || 'temperatureLessThanOrEqualToUserUnits' in rule) &&
    'measurementHeights' in rule &&
    'includeAllDevices' in rule
  ) {
    return true
  }

  return false
}

// Check if at least one rule is defined
export const WeatherForecastAlertForm = ({
  alertId,
  onClose,
  initialState,
  selectedAlert,
  setIsSubmitting,
  setSubmitHandler,
  refreshAlerts,
}: {
  opened: boolean
  alertId: string | null
  onClose: () => void
  initialState: WeatherForecastAlertFormValues
  selectedAlert: AlertWithPossibleId | null
  submitting: boolean
  setIsSubmitting: (isSubmitting: boolean) => void
  setSubmitHandler: (handler: () => unknown) => void
  refreshAlerts: () => void
}) => {
  // STATE for THRESHOLD
  const [hourOfDay, setHourOfDay] = useState<string | null>(
    initialState.hourOfDay ?? AlertsSettings.weatherForecastDefaultHourOfDay,
  )

  const [rule, setRule] = useState<Partial<WeatherForecastAlertOnly['rules']>>(
    isEmpty(initialState.rules) ? { windGreaterThanOrEqualToUserUnits: undefined } : initialState.rules ?? {},
  )

  const [thresholdIsValid, setThresholdIsValid] = useState(false)
  // STATE for COVERAGE
  const [coverageIsValid, setCoverageIsValid] = useState(false)
  const [coverageProperties, setCoverageProperties] = useState<number[]>(initialState.coverageProperties)
  const [coverageStations, setCoverageStations] = useState<string[]>(initialState.coverageStations)
  const [coverageBlocks, setCoverageBlocks] = useState<string[]>(initialState.coverageBlocks)
  const [coverageType, setCoverageType] = useState(initialState.coverageType)
  // STATE FOR Scheduling
  const [scheduleIsValid, setScheduleIsValid] = useState(true)

  const [limitedSchedule, setLimitedSchedule] = useState(
    !!(selectedAlert && selectedAlert.startDate && selectedAlert.endDate),
  )

  const [endDate, setEndDate] = useState<Moment | null>(initialState.endDate)
  const [startDate, setStartDate] = useState<Moment | null>(initialState.startDate)
  // STATE FOR Contacts
  const [contacts, setContacts] = useState<Contact[]>(initialState.contacts)
  const [groups, setGroups] = useState<Group[]>(initialState.groups)
  const [notifyOwnerByEmail, setNotifyOwnerByEmail] = useState(initialState.notifyOwnerByEmail)
  const [notifyOwnerBySMS, setNotifyOwnerBySMS] = useState(initialState.notifyOwnerBySMS)
  const [contactsIsValid, setContactsIsValid] = useState(true)
  // STATE FOR Name
  const [nameIsValid, setNameIsValid] = useState(false)
  const [name, setName] = useState<string | null>(initialState.name)
  // STATE FOR KEEPING TRACK OF WHETHER OR NOT WE SENT IT YET
  const [triedToSubmit, setTriedToSubmit] = useState(false)
  const { contacts: allContacts, groups: allGroups } = useGroupsAndContacts()

  useEffect(() => {
    const updatedGroups = groups
      .map((group) => {
        const groupInAllGroups = allGroups.find((g) => g.id === group.id)

        if (groupInAllGroups) return { ...groupInAllGroups, alertsContactMethods: group.alertsContactMethods }

        return null
      })
      .filter((g) => g !== null)

    const updatedContacts = contacts
      .map((contact) => {
        const contactInAllContacts = allContacts.find((c) => c.id === contact.id)

        if (contactInAllContacts)
          return { ...contactInAllContacts, alertsContactMethods: contact.alertsContactMethods }

        return null
      })
      .filter((c) => c !== null)

    setGroups(updatedGroups)

    setContacts(updatedContacts)
  }, [allContacts, allGroups])

  const handleSubmit = useCallback(async () => {
    const configurationIsValid =
      thresholdIsValid &&
      coverageIsValid &&
      scheduleIsValid &&
      contactsIsValid &&
      nameIsValid &&
      !!name &&
      !!hourOfDay &&
      isRuleValid(rule)

    if (!triedToSubmit && !configurationIsValid) {
      setTriedToSubmit(true)
    }

    if (configurationIsValid) {
      const variables = getAlertVariablesForWeatherForecastQuery({
        contacts,
        coverageBlocks,
        coverageProperties,
        coverageStations,
        coverageType,
        endDate,
        groups,
        hourOfDay,
        name,
        notifyOwnerByEmail,
        notifyOwnerBySMS,
        rules: rule,
        startDate,
      })

      const body: routes.LegacyWeatherForecastAlertsManagement.Request = {}

      if (alertId) {
        body.legacyWeatherForecastAlertsToUpdate = {
          [alertId]: variables,
        }
      } else {
        body.legacyWeatherForecastAlertsToCreate = [variables]
      }

      const successText = !alertId
        ? translate.phrases.banyanApp('Alert successfully created')
        : translate.phrases.banyanApp('Alert successfully updated')

      const errorText = !alertId
        ? translate.phrases.banyanApp('Failed to create alert')
        : translate.phrases.banyanApp('Failed to update alert')

      setIsSubmitting(true)

      try {
        const response = await apiFetch({
          url: '/legacy-weather-forecast-alerts-management',
          body,
        })

        const { data, errors } = response
        const newAlertId = alertId ?? Object.keys(data.legacyWeatherForecastAlertsToCreate ?? {})[0]

        if ((errors && errors.length) || !newAlertId) {
          showNotification({
            type: 'error',
            message: errorText,
          })
        } else {
          showNotification({
            type: 'success',
            message: successText,
          })

          refreshAlerts()
        }

        onClose()
      } catch (error) {
        showNotification({
          type: 'error',
          message: errorText,
        })

        onClose()
      }

      setIsSubmitting(false)
    }
  }, [
    thresholdIsValid,
    coverageIsValid,
    scheduleIsValid,
    contactsIsValid,
    nameIsValid,
    rule,
    contacts,
    groups,
    coverageBlocks,
    coverageProperties,
    coverageStations,
    coverageType,
    endDate,
    hourOfDay,
    name,
    notifyOwnerByEmail,
    notifyOwnerBySMS,
    startDate,
    alertId,
    triedToSubmit,
    setIsSubmitting,
    onClose,
    refreshAlerts,
  ])

  useEffect(() => {
    setSubmitHandler(() => handleSubmit)
  }, [handleSubmit])

  return (
    <>
      <AlertSection>
        <WeatherForecastThreshold
          key={Object.keys(rule).join('')}
          rule={rule}
          setRule={setRule}
          hourOfDay={hourOfDay}
          setHourOfDay={setHourOfDay}
          setThresholdIsValid={setThresholdIsValid}
          triedToSubmit={triedToSubmit}
        />
      </AlertSection>

      <WeatherForecastCoverage
        coverageBlocks={coverageBlocks}
        coverageIsValid={coverageIsValid}
        coverageProperties={coverageProperties}
        coverageStations={coverageStations}
        coverageType={coverageType}
        isTemperatureTypeAlert={
          'temperatureGreaterThanOrEqualToUserUnits' in rule ||
          'temperatureLessThanOrEqualToUserUnits' in rule
        }
        rule={rule}
        setCoverageBlocks={setCoverageBlocks}
        setCoverageIsValid={setCoverageIsValid}
        setCoverageProperties={setCoverageProperties}
        setCoverageStations={setCoverageStations}
        setCoverageType={setCoverageType}
        setRule={setRule}
        triedToSubmit={triedToSubmit}
      />

      <Scheduling
        alertTypeId="weatherForecastAlert"
        endDate={endDate}
        limitedSchedule={limitedSchedule}
        setEndDate={setEndDate}
        setLimitedSchedule={setLimitedSchedule}
        setScheduleIsValid={setScheduleIsValid}
        setStartDate={setStartDate}
        startDate={startDate}
        triedToSubmit={triedToSubmit}
      />
      <Contacts
        contacts={contacts}
        contactsIsValid={contactsIsValid}
        groups={groups}
        notifyOwnerByEmail={notifyOwnerByEmail}
        notifyOwnerBySMS={notifyOwnerBySMS}
        setContacts={setContacts}
        setContactsIsValid={setContactsIsValid}
        setGroups={setGroups}
        setNotifyOwnerByEmail={setNotifyOwnerByEmail}
        setNotifyOwnerBySMS={setNotifyOwnerBySMS}
      />
      <Name
        alertTypeId="weatherForecastAlert"
        name={name}
        setName={setName}
        setNameIsValid={setNameIsValid}
        triedToSubmit={triedToSubmit}
      />
    </>
  )
}
